// ─────────────────────────────────────────────────────────────────────────────
// graphCamera — focus flight and the focus stack.
//
// THE CAMERA NEVER TELEPORTS. Arriving somewhere instantly costs the user the
// one thing a spatial map is for: knowing where the new place is relative to the
// old one. Every focus is a flight, and the flight is what teaches the geography.
//
// OrbitControls stays the base interaction; this drives position and target for
// the duration of a tween and then hands them straight back. Controls are
// disabled while a flight is in the air so a stray drag cannot fight it.
// ─────────────────────────────────────────────────────────────────────────────

import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { focusDistanceFor } from './graphLod';
import { RexyGraphNodeKind } from './graphTypes';

export const FOCUS_FLIGHT_MS = 900;

export function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

interface Flight {
  generation: number;
  fromPos: THREE.Vector3;
  toPos: THREE.Vector3;
  fromTarget: THREE.Vector3;
  toTarget: THREE.Vector3;
  start: number;
  done: () => void;
}

export class GraphCameraDirector {
  private flight: Flight | null = null;
  /**
   * Bumped on every start AND every cancel. A tween update whose generation is
   * stale returns immediately, so a queued frame can never resurrect a flight
   * the owner has already taken over.
   */
  private generation = 0;
  private flying = false;

  /** REXY → TRADING → MACK → MEMORY → FILE. Back pops one and flies to it. */
  readonly focusStack: string[] = [];

  constructor(
    private camera: THREE.PerspectiveCamera,
    private controls: OrbitControls,
  ) {}

  /** Synchronous. Reading this immediately after cancel() must give false. */
  get isFlying(): boolean {
    return this.flying;
  }

  /**
   * Fly to a node. The approach keeps the CURRENT viewing direction and simply
   * closes the distance — swinging to a canonical angle would spin the world
   * under the user for no information.
   */
  flyTo(
    position: [number, number, number],
    kind: RexyGraphNodeKind,
    now: number,
    push?: string,
  ): Promise<void> {
    const target = new THREE.Vector3(position[0], position[1], position[2]);
    const distance = focusDistanceFor(kind);

    const direction = new THREE.Vector3().subVectors(this.camera.position, this.controls.target);
    if (direction.lengthSq() < 1e-6) direction.set(0, 0.15, 1);
    direction.normalize();

    const toPos = new THREE.Vector3().copy(target).addScaledVector(direction, distance);

    if (push) {
      const existing = this.focusStack.indexOf(push);
      if (existing >= 0) this.focusStack.length = existing; // returning UP the stack
      this.focusStack.push(push);
    }

    // CONTROLS ARE NEVER DISABLED DURING A FLIGHT.
    //
    // They used to be, and that was the overshoot bug: cancelling mid-drag
    // re-enabled OrbitControls in the middle of a gesture whose internal start
    // position was thousands of units stale, and the next pointermove applied
    // the whole accumulated delta at once — the measured 7,575-unit jump. With
    // controls left enabled there is no stale gesture to resume, and manual
    // input simply cancels the flight on its first event.
    const generation = ++this.generation;
    this.flying = true;
    return new Promise<void>((resolve) => {
      this.flight = {
        generation,
        fromPos: this.camera.position.clone(),
        toPos,
        fromTarget: this.controls.target.clone(),
        toTarget: target,
        start: now,
        done: resolve,
      };
    });
  }

  /** Called once per frame. Returns true while a flight is still in the air. */
  update(now: number): boolean {
    const f = this.flight;
    if (!f) return false;
    // Stale generation — this flight was cancelled between frames.
    if (f.generation !== this.generation) {
      this.flight = null;
      this.flying = false;
      return false;
    }

    const t = Math.min(1, (now - f.start) / FOCUS_FLIGHT_MS);
    const k = easeInOutCubic(t);

    this.camera.position.lerpVectors(f.fromPos, f.toPos, k);
    this.controls.target.lerpVectors(f.fromTarget, f.toTarget, k);
    this.controls.update();

    if (t >= 1) {
      this.flight = null;
      this.flying = false;
      f.done();
      return false;
    }
    return true;
  }

  /** Distance from the camera to whatever it is currently looking at. */
  distanceToTarget(): number {
    return this.camera.position.distanceTo(this.controls.target);
  }

  /**
   * End the flight where it currently is. Idempotent, and it MOVES NOTHING: the
   * camera and target keep the exact interpolated values of the last frame, so
   * there is no snap to the start and no snap to the destination.
   */
  cancel(reason?: string): void {
    // Idempotent and SYNCHRONOUS: generation moves first, so any frame already
    // queued sees a stale token and does nothing.
    this.generation++;
    this.flying = false;
    if (!this.flight) return;
    const done = this.flight.done;
    this.flight = null;
    this.lastCancelReason = reason || 'manual';
    done();
  }

  /** Dev-only: why the last flight ended. */
  lastCancelReason = '';
}
